import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { EntityKpiDenominatorDto } from 'app/@types/dto/entity-kpi-denominator-dto.model';
import { OrganizationKpiDenominatorTypeDto } from 'app/@types/dto/organization-kpi-denominator-type-dto.model';

/** One edited cell of the matrix; the parent adds the entity id before saving. */
export interface KpiDenominatorCellValue {
    refYear: number;
    kpiDenominatorTypeId: string;
    value: number;
}

/**
 * Editable (KPI type × year) grid of denominator values for a single entity.
 * Presentational: the parent owns loading and saving; this component only
 * turns `values` into a reactive form and emits the current cell values on
 * every edit (plus whether anything differs from the initial values).
 */
@Component({
    selector: 'app-kpi-denominator-matrix',
    templateUrl: './kpi-denominator-matrix.component.html',
    styleUrls: ['./kpi-denominator-matrix.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class KpiDenominatorMatrixComponent implements OnChanges, OnDestroy {
    @Input() types: OrganizationKpiDenominatorTypeDto[] = [];
    /** Years to show as columns; rendered in the order given. */
    @Input() years: number[] = [];
    /** Stored denominator values of the entity being edited. */
    @Input() values: EntityKpiDenominatorDto[] = [];
    @Input() disabled = false;

    @Output() cellsChange = new EventEmitter<{ cells: KpiDenominatorCellValue[]; dirty: boolean }>();

    matrixForm: FormGroup<Record<string, FormControl<number | null>>> = new FormGroup<
        Record<string, FormControl<number | null>>
    >({});

    private initialByKey = new Map<string, number>();
    private rebuild$ = new Subject<void>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['types'] || changes['years'] || changes['values']) {
            this.buildForm();
        }
        if (changes['disabled']) {
            this.applyDisabledState();
        }
    }

    ngOnDestroy(): void {
        this.rebuild$.next();
        this.rebuild$.complete();
    }

    controlKey(typeId: string, year: number): string {
        return `${typeId}|${year}`;
    }

    private buildForm(): void {
        this.rebuild$.next();
        this.initialByKey = new Map(
            this.values.map((v) => [this.controlKey(v.kpiDenominatorTypeId, v.refYear), v.value]),
        );

        const controls: Record<string, FormControl<number | null>> = {};
        for (const type of this.types) {
            if (!type.id) {
                continue;
            }
            for (const year of this.years) {
                const key = this.controlKey(type.id, year);
                controls[key] = new FormControl<number | null>(this.initialByKey.get(key) ?? null);
            }
        }
        this.matrixForm = new FormGroup(controls);
        this.applyDisabledState();

        this.matrixForm.valueChanges.pipe(takeUntil(this.rebuild$)).subscribe(() => this.emitCells());
    }

    private applyDisabledState(): void {
        if (this.disabled) {
            this.matrixForm.disable({ emitEvent: false });
        } else {
            this.matrixForm.enable({ emitEvent: false });
        }
    }

    private emitCells(): void {
        const cells: KpiDenominatorCellValue[] = [];
        let dirty = false;
        for (const [key, control] of Object.entries(this.matrixForm.controls)) {
            const [kpiDenominatorTypeId, year] = key.split('|');
            const initial = this.initialByKey.get(key);
            // A cleared cell that had a stored value is saved as 0 (there is no delete
            // endpoint); the KPI computation treats a 0 denominator as "no KPI".
            const value = control.value ?? (initial !== undefined ? 0 : null);
            if (value === null) {
                continue;
            }
            if (value !== initial) {
                dirty = true;
            }
            cells.push({ refYear: Number(year), kpiDenominatorTypeId, value });
        }
        this.cellsChange.emit({ cells, dirty });
    }

    trackByType(index: number, type: OrganizationKpiDenominatorTypeDto): string {
        return type.id ?? `idx-${index}`;
    }

    trackByYear(_index: number, year: number): number {
        return year;
    }
}
